
const ANNOTATION_HEADROOM_PERCENT = 0.1;
const MIN_ANNOTATION_HEADROOM = 1;
const AUTO_WIDTH_CATEGORY_THRESHOLD = 20;
const PIXELS_PER_CATEGORY = 50;
const X_AXIS_LABEL_SLANT_ANGLE = 60;
const MAX_LABEL_WIDTH_PX = 120;
const BASE_X_AXIS_PADDING_PX = 26;
const CHART_AREA_BOTTOM = Math.ceil(
    MAX_LABEL_WIDTH_PX * Math.sin((X_AXIS_LABEL_SLANT_ANGLE * Math.PI) / 180) + BASE_X_AXIS_PADDING_PX,
);
type ChartDataRow = Array<string | number>;
type BarChartTextStyle = {
    fontSize: number;
    bold?: boolean;
    left?: number;
    cursor?: string;
    color?: string;
    underline?: boolean;
    linkSuggested?: boolean;
};
type BarChartOptions = {
    width?: number;
    titleTextStyle: BarChartTextStyle;
    chartArea: {
        top: number;
        bottom: number;
        right: number;
        left: number;
    };
    colors: string[];
    bar: { groupWidth: string };
    legend: 'none';
    annotations: {
        alwaysOutside: boolean;
        stem: {
            color: string;
            length: number;
        };
        textStyle: {
            bold: boolean;
            color: string;
            fontSize: number;
        };
    };
    tooltip: {
        trigger: 'both';
    };
    chart: {
        textStyle: {
            fontSize: number;
            bold: boolean;
        };
    };
    vAxis: {
        title: string;
        titleTextStyle: { italic: boolean; bold: boolean };
        minValue: number;
        viewWindow: {
            min: number;
            max: number;
        };
        ticks?: number[];
        textStyle: {
            fontSize: number;
        };
        format: string;
    };
    hAxis: {
        textPosition: 'out';
        titleTextStyle: { italic: boolean; bold: boolean };
        minValue: number;
        textStyle: BarChartTextStyle;
        slantedText: boolean;
        slantedTextAngle: number;
        showTextEvery: number;
    };
    axes: {
        y: {
            0: { side: 'right' };
        };
    };
};

export const barChartOptions = (
    hTitle: string,
    chartColors: string[],
    _chartType: string,
    chartData: ChartDataRow[],
    isDrillDownAllowed: boolean,
    annotationColor: string,
): BarChartOptions => {


    let linkStyle: Pick<BarChartTextStyle, 'cursor' | 'color' | 'underline' | 'linkSuggested'> = {};
    if (isDrillDownAllowed) {
        linkStyle = {
            cursor: 'pointer',
            color: annotationColor,
            underline: true,
            linkSuggested: true,
        };
    }

    const width =
        Number(chartData.length) > AUTO_WIDTH_CATEGORY_THRESHOLD
            ? Number(chartData.length) * PIXELS_PER_CATEGORY
            : undefined;
    const chartDataArray = chartData
        .map(v => v.filter((value): value is number => typeof value === 'number' && !Number.isNaN(value)))
        .map(v => v.filter(value => value > 0))
        .filter(v => v.length > 0);
    const tickValue = chartDataArray?.length || 0;
    const maxBarValue = tickValue ? Math.max(...chartDataArray.flat()) : 0;
    const annotationHeadroom = maxBarValue > 0
        ? Math.max(MIN_ANNOTATION_HEADROOM, Math.ceil(maxBarValue * ANNOTATION_HEADROOM_PERCENT))
        : MIN_ANNOTATION_HEADROOM;
    const vAxisMax = tickValue ? maxBarValue + annotationHeadroom : 2;

    return {
        ...(typeof width === 'number' ? { width } : {}),
        titleTextStyle: {
            fontSize: 15,
            bold: true,
            left: 10,
        },
        chartArea: {
            top: 50,
            bottom: CHART_AREA_BOTTOM,
            right: 100,
            left: 150,
        },
        colors: chartColors,
        bar: { groupWidth: '90%' },
        legend: 'none',
        annotations: {
            alwaysOutside: true,
            stem: {
                color: 'transparent',
                length: 6,
            },
            textStyle: {
                bold: true,
                color: annotationColor,
                fontSize: 14,
            },
        },
        tooltip: {
            trigger: 'both',
        },
        chart: {
            textStyle: {
                fontSize: 13,
                bold: true,
            },
        },
        vAxis: {
            title: hTitle,
            titleTextStyle: { italic: false, bold: true },
            minValue: 0,
            viewWindow: {
                min: 0,
                max: vAxisMax,
            },
            ticks: !tickValue ? [0, 1, 2] : undefined,
            textStyle: {
                fontSize: 13,
            },
            format: '',
        },
        hAxis: {
            textPosition: 'out',
            titleTextStyle: { italic: false, bold: true },
            minValue: 0,
            textStyle: {
                fontSize: 13,
                ...linkStyle,
            },
            slantedText: true,
            slantedTextAngle: X_AXIS_LABEL_SLANT_ANGLE,
            showTextEvery: 1,
        },
        axes: {
            y: {
                0: { side: 'right' },
            },
        },
    };
};
